import axios from 'axios';
import {
    VAULT_PATIENT_LIST_REQUEST, VAULT_PATIENT_LIST_SUCCESS, VAULT_PATIENT_LIST_FAIL,
    VAULT_PATIENT_LIST_BY_IDS_REQUEST, VAULT_PATIENT_LIST_BY_IDS_SUCCESS, VAULT_PATIENT_LIST_BY_IDS_FAIL,
    VAULT_PATIENT_DETAILS_REQUEST, VAULT_PATIENT_DETAILS_SUCCESS, VAULT_PATIENT_DETAILS_FAIL,
    VAULT_PATIENT_UPDATE_REQUEST, VAULT_PATIENT_UPDATE_SUCCESS, VAULT_PATIENT_UPDATE_FAIL,
    VAULT_PATIENT_DTO_REQUEST, VAULT_PATIENT_DTO_SUCCESS, VAULT_PATIENT_DTO_FAIL,
    VAULT_PATIENT_OPT_OUT_REQUEST, VAULT_PATIENT_OPT_OUT_SUCCESS, VAULT_PATIENT_OPT_OUT_FAIL,
    VAULT_PATIENT_BACKFILL_REQUEST, VAULT_PATIENT_BACKFILL_SUCCESS, VAULT_PATIENT_BACKFILL_FAIL,
    VAULT_PATIENT_UPLOAD_BATCHES_REQUEST, VAULT_PATIENT_UPLOAD_BATCHES_SUCCESS, VAULT_PATIENT_UPLOAD_BATCHES_FAIL,
    VAULT_PATIENT_SUB_STUDIES_REQUEST, VAULT_PATIENT_SUB_STUDIES_SUCCESS, VAULT_PATIENT_SUB_STUDIES_FAIL,
    VAULT_SUB_STUDY_LIST_REQUEST, VAULT_SUB_STUDY_LIST_SUCCESS, VAULT_SUB_STUDY_LIST_FAIL,
    VAULT_SUB_STUDY_LIST_BY_IDS_REQUEST, VAULT_SUB_STUDY_LIST_BY_IDS_SUCCESS, VAULT_SUB_STUDY_LIST_BY_IDS_FAIL,
    VAULT_SUB_STUDY_DETAILS_REQUEST, VAULT_SUB_STUDY_DETAILS_SUCCESS, VAULT_SUB_STUDY_DETAILS_FAIL,
    VAULT_SUB_STUDY_DTO_REQUEST, VAULT_SUB_STUDY_DTO_SUCCESS, VAULT_SUB_STUDY_DTO_FAIL,
    VAULT_SUB_STUDY_CREATE_REQUEST, VAULT_SUB_STUDY_CREATE_SUCCESS, VAULT_SUB_STUDY_CREATE_FAIL,
    VAULT_SUB_STUDY_UPDATE_REQUEST, VAULT_SUB_STUDY_UPDATE_SUCCESS, VAULT_SUB_STUDY_UPDATE_FAIL,
    VAULT_SUB_STUDY_DELETE_REQUEST, VAULT_SUB_STUDY_DELETE_SUCCESS, VAULT_SUB_STUDY_DELETE_FAIL,
    VAULT_SUB_STUDY_PATIENTS_REQUEST, VAULT_SUB_STUDY_PATIENTS_SUCCESS, VAULT_SUB_STUDY_PATIENTS_FAIL,
} from '../constants/identifierVaultConstants';
import { formatApiError } from '../utils/formatApiError';

const getAuthConfig = (token) => ({
    headers: {
        'Content-type': 'application/json',
        'Authorization': `Bearer ${token}`
    }
});

const formatErrorMessage = (error) => {
    if (error.response?.status === 403) {
        // formatApiError already embeds policy when present — do not append again.
        return [formatApiError(error)];
    }
    const errorMessages = [];
    if (error.response?.data) {
        if (error.response.data.errors) {
            Object.entries(error.response.data.errors).forEach(([key, value]) => {
                errorMessages.push(`${key}: ${value[0]}`);
            });
        }
        if (error.response.data.detail) {
            errorMessages.push(`Detail: ${error.response.data.detail}`);
        }
        if (error.response.data.message) {
            errorMessages.push(`Message: ${error.response.data.message}`);
        }
    }
    return errorMessages.length > 0 ? errorMessages : [error.message || "An error occurred, please try again later"];
};

const API_BASE_URL = '/dotnet_api/v1/vaultpatient';

// Note: there is deliberately no createVaultPatient or deleteVaultPatient. Participants are
// minted by VaultPatientRepository.ResolveAsync when a Patient is saved, and protocol §5.3
// forbids deleting them (the controller suppresses Delete with [NonAction]); withdrawal goes
// through setVaultPatientOptOut below.

export const listVaultPatients = (params = {}) => async (dispatch, getState) => {
    try {
        dispatch({ type: VAULT_PATIENT_LIST_REQUEST });
        const config = {
            ...getAuthConfig(getState().userLogin.userInfo.token),
            params
        };
        const { data } = await axios.get(`${API_BASE_URL}/all`, config);
        dispatch({ type: VAULT_PATIENT_LIST_SUCCESS, payload: data });
    } catch (error) {
        dispatch({ type: VAULT_PATIENT_LIST_FAIL, payload: formatErrorMessage(error) });
    }
};

export const listVaultPatientsByIds = (ids) => async (dispatch, getState) => {
    try {
        dispatch({ type: VAULT_PATIENT_LIST_BY_IDS_REQUEST });
        const { data } = await axios.post(
            `${API_BASE_URL}/ids/all`,
            ids,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: VAULT_PATIENT_LIST_BY_IDS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({ type: VAULT_PATIENT_LIST_BY_IDS_FAIL, payload: formatErrorMessage(error) });
    }
};

export const getVaultPatientDetails = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: VAULT_PATIENT_DETAILS_REQUEST });
        const { data } = await axios.get(
            `${API_BASE_URL}/${id}`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: VAULT_PATIENT_DETAILS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({ type: VAULT_PATIENT_DETAILS_FAIL, payload: formatErrorMessage(error) });
    }
};

export const updateVaultPatient = (id, changes) => async (dispatch, getState) => {
    try {
        dispatch({ type: VAULT_PATIENT_UPDATE_REQUEST });
        const { data } = await axios.patch(
            `${API_BASE_URL}/${id}`,
            changes,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: VAULT_PATIENT_UPDATE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({ type: VAULT_PATIENT_UPDATE_FAIL, payload: formatErrorMessage(error) });
    }
};

export const loadVaultPatientDto = () => async (dispatch, getState) => {
    try {
        dispatch({ type: VAULT_PATIENT_DTO_REQUEST });
        const { data } = await axios.get(
            `${API_BASE_URL}/dto`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: VAULT_PATIENT_DTO_SUCCESS, payload: data });
    } catch (error) {
        dispatch({ type: VAULT_PATIENT_DTO_FAIL, payload: formatErrorMessage(error) });
    }
};

/** Protocol §5.3 withdrawal. optOutSource is one of NDOO | CareTeam | StudyWebsite. */
export const setVaultPatientOptOut = (id, optOutSource) => async (dispatch, getState) => {
    try {
        dispatch({ type: VAULT_PATIENT_OPT_OUT_REQUEST });
        const { data } = await axios.post(
            `${API_BASE_URL}/${id}/opt-out`,
            { optOutSource },
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: VAULT_PATIENT_OPT_OUT_SUCCESS, payload: data });
    } catch (error) {
        dispatch({ type: VAULT_PATIENT_OPT_OUT_FAIL, payload: formatErrorMessage(error) });
    }
};

/** Recomputes Study Hashes for every Patient in the calling tenant. Idempotent. */
export const backfillStudyHashes = () => async (dispatch, getState) => {
    try {
        dispatch({ type: VAULT_PATIENT_BACKFILL_REQUEST });
        const { data } = await axios.post(
            `${API_BASE_URL}/backfill`,
            {},
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: VAULT_PATIENT_BACKFILL_SUCCESS, payload: data });
    } catch (error) {
        dispatch({ type: VAULT_PATIENT_BACKFILL_FAIL, payload: formatErrorMessage(error) });
    }
};

export const getVaultPatientUploadBatches = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: VAULT_PATIENT_UPLOAD_BATCHES_REQUEST });
        const { data } = await axios.get(
            `${API_BASE_URL}/${id}/upload-batches`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: VAULT_PATIENT_UPLOAD_BATCHES_SUCCESS, payload: data });
    } catch (error) {
        dispatch({ type: VAULT_PATIENT_UPLOAD_BATCHES_FAIL, payload: formatErrorMessage(error) });
    }
};

export const getVaultPatientSubStudies = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: VAULT_PATIENT_SUB_STUDIES_REQUEST });
        const { data } = await axios.get(
            `${API_BASE_URL}/${id}/sub-studies`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: VAULT_PATIENT_SUB_STUDIES_SUCCESS, payload: data });
    } catch (error) {
        dispatch({ type: VAULT_PATIENT_SUB_STUDIES_FAIL, payload: formatErrorMessage(error) });
    }
};

// VaultSubStudy CRUD
const SUB_STUDY_API_BASE_URL = '/dotnet_api/v1/vaultsubstudy';

export const listVaultSubStudies = (params = {}) => async (dispatch, getState) => {
    try {
        dispatch({ type: VAULT_SUB_STUDY_LIST_REQUEST });
        const config = {
            ...getAuthConfig(getState().userLogin.userInfo.token),
            params
        };
        const { data } = await axios.get(`${SUB_STUDY_API_BASE_URL}/all`, config);
        dispatch({ type: VAULT_SUB_STUDY_LIST_SUCCESS, payload: data });
    } catch (error) {
        dispatch({ type: VAULT_SUB_STUDY_LIST_FAIL, payload: formatErrorMessage(error) });
    }
};

export const listVaultSubStudiesByIds = (ids) => async (dispatch, getState) => {
    try {
        dispatch({ type: VAULT_SUB_STUDY_LIST_BY_IDS_REQUEST });
        const { data } = await axios.post(
            `${SUB_STUDY_API_BASE_URL}/ids/all`,
            ids,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: VAULT_SUB_STUDY_LIST_BY_IDS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({ type: VAULT_SUB_STUDY_LIST_BY_IDS_FAIL, payload: formatErrorMessage(error) });
    }
};

export const getVaultSubStudyDetails = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: VAULT_SUB_STUDY_DETAILS_REQUEST });
        const { data } = await axios.get(
            `${SUB_STUDY_API_BASE_URL}/${id}`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: VAULT_SUB_STUDY_DETAILS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({ type: VAULT_SUB_STUDY_DETAILS_FAIL, payload: formatErrorMessage(error) });
    }
};

export const loadVaultSubStudyDto = () => async (dispatch, getState) => {
    try {
        dispatch({ type: VAULT_SUB_STUDY_DTO_REQUEST });
        const { data } = await axios.get(
            `${SUB_STUDY_API_BASE_URL}/dto`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: VAULT_SUB_STUDY_DTO_SUCCESS, payload: data });
    } catch (error) {
        dispatch({ type: VAULT_SUB_STUDY_DTO_FAIL, payload: formatErrorMessage(error) });
    }
};

export const createVaultSubStudy = (vaultSubStudy) => async (dispatch, getState) => {
    try {
        dispatch({ type: VAULT_SUB_STUDY_CREATE_REQUEST });
        const url = SUB_STUDY_API_BASE_URL;
        const { data } = await axios.post(
            url,
            vaultSubStudy,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: VAULT_SUB_STUDY_CREATE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({ type: VAULT_SUB_STUDY_CREATE_FAIL, payload: formatErrorMessage(error) });
    }
};

export const updateVaultSubStudy = (id, changes) => async (dispatch, getState) => {
    try {
        dispatch({ type: VAULT_SUB_STUDY_UPDATE_REQUEST });
        const { data } = await axios.patch(
            `${SUB_STUDY_API_BASE_URL}/${id}`,
            changes,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: VAULT_SUB_STUDY_UPDATE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({ type: VAULT_SUB_STUDY_UPDATE_FAIL, payload: formatErrorMessage(error) });
    }
};

export const deleteVaultSubStudy = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: VAULT_SUB_STUDY_DELETE_REQUEST });
        await axios.delete(
            `${SUB_STUDY_API_BASE_URL}/${id}`,
            getAuthConfig(getState().userLogin.userInfo.token)
        );
        dispatch({ type: VAULT_SUB_STUDY_DELETE_SUCCESS, payload: id });
    } catch (error) {
        dispatch({ type: VAULT_SUB_STUDY_DELETE_FAIL, payload: formatErrorMessage(error) });
    }
};

/** Insert-only enrol — never replaces the full join set. */
export const enrolVaultPatientsInSubStudy = (subStudyId, vaultPatientIds) => async (_dispatch, getState) => {
    await axios.post(
        `${SUB_STUDY_API_BASE_URL}/${subStudyId}/enrolments`,
        vaultPatientIds,
        getAuthConfig(getState().userLogin.userInfo.token)
    );
};

export const unenrolVaultPatientFromSubStudy = (subStudyId, vaultPatientId) => async (_dispatch, getState) => {
    await axios.delete(
        `${SUB_STUDY_API_BASE_URL}/${subStudyId}/enrolments/${vaultPatientId}`,
        getAuthConfig(getState().userLogin.userInfo.token)
    );
};

/** Paginated VaultPatients enrolled in one sub-study (mirrors GetAll query shape). */
export const listVaultSubStudyPatients = (subStudyId, params = {}) => async (dispatch, getState) => {
    try {
        dispatch({ type: VAULT_SUB_STUDY_PATIENTS_REQUEST });
        const config = {
            ...getAuthConfig(getState().userLogin.userInfo.token),
            params,
        };
        const { data } = await axios.get(
            `${SUB_STUDY_API_BASE_URL}/${subStudyId}/patients`,
            config
        );
        dispatch({ type: VAULT_SUB_STUDY_PATIENTS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({ type: VAULT_SUB_STUDY_PATIENTS_FAIL, payload: formatErrorMessage(error) });
    }
};
